<!--
  智能知识库AI问答主界面
  - 左侧为会话列表（可折叠），支持新建、重命名、删除会话
  - 右侧为聊天区或文件详情区，根据 viewMode 切换
  - 聊天区支持流式AI回复、文件引用、推荐问题等
  - 纯HTML+CSS布局，Element UI仅用于部分控件
-->
<template>
	<div class="register-main knowledge-base-ai">
		<!-- 折叠后展开按钮，浮在右侧内容区左侧中间 -->
		<div v-if="isCollapsed" class="sidebar-expand-btn">
			<img src="@/assets/images/ai.svg" alt="AI" style="width:38px;height:38px;display: block;margin: 0 auto 10px auto;" />
			<i class="el-icon-s-unfold" @click="toggleCollapse" style="display: block; margin: 0 auto 10px auto; cursor: pointer;"></i>
			<img
				src="@/assets/images/add-chat.svg"
				alt="新建对话"
				style="width:25px;height:25px;display: block;margin: 0 auto;cursor:pointer;"
				@click="createSession"
			/>
		</div>
		<!-- 左侧会话列表区域 (可折叠隐藏) -->
		<div v-if="!isCollapsed" :class="['sidebar', { collapsed: isCollapsed }]">
			<div class="sidebar-header">
				<div style="display: flex; align-items: center;">
					<img src="@/assets/images/ai.svg" alt="AI" style="width:38px;height:38px;margin-right:10px;" />
					<span class="sidebar-title">智能问答</span>
				</div>
				<i class="el-icon-s-fold collapse-btn" @click="toggleCollapse"></i>
			</div>
			<div class="sidebar-new-session">
				<button class="new-session-btn" @click="createSession"><i class="el-icon-plus"></i> 新建对话</button>
			</div>
			<div v-if="sessions.length > 0" class="sidebar-history-title"><i class="el-icon-chat-dot-round"></i> 历史对话</div>
			<ul v-if="sessions.length > 0" class="session-list">
				<li v-for="session in sessions" :key="session.id" :class="['session-item', { active: session.id === activeSessionId }]">
					<template v-if="renamingSessionId === session.id">
						<input v-model="session.title" class="rename-input" @blur="finishRename" @keyup.enter.prevent="finishRename" v-focus />
					</template>
					<template v-else>
						<el-tooltip class="item" effect="dark" :content="session.title" placement="top" :open-delay="500">
							<span class="session-title" @click="handleSessionSelect(session.id)">
								{{ session.title }}
							</span>
						</el-tooltip>
						<el-dropdown trigger="click" @command="handleCommand" class="actions-dropdown">
							<span class="el-dropdown-link actions-trigger">
								<i class="el-icon-more"></i>
							</span>
							<el-dropdown-menu slot="dropdown">
								<el-dropdown-item :command="{ action: 'rename', session: session }">
									<i class="el-icon-edit"></i> 重命名
								</el-dropdown-item>
								<el-dropdown-item :command="{ action: 'delete', id: session.id }">
									<i class="el-icon-delete"></i> 删除
								</el-dropdown-item>
							</el-dropdown-menu>
						</el-dropdown>
					</template>
				</li>
			</ul>
		</div>

		<!-- 右侧内容区域: 根据 viewMode 切换 -->
		<!-- 聊天视图 -->
		<div v-if="viewMode === 'chat'" class="chat-content">
			<div v-if="!activeSession" class="welcome-center">
				<div class="welcome">
					<h2>海量知识库，等你来查</h2>
				</div>
				<div class="welcome-input">
					<div class="select-tag" v-if="!!selectNames">
						<el-tag closable @close="clearSelectLib">{{ selectNames }}</el-tag>
						<span>（查询限定范围）</span>
					</div>
					<el-popover placement="top-start" trigger="manual" v-model="inputVisible" popper-class="lib-popover">
						<el-cascader-panel
							v-model="selectLibs"
							:options="treeList"
							:props="{ checkStrictly: true, expandTrigger: 'hover', label: 'sname', value: 'id' }"
							@change="handleChangeFw"
						></el-cascader-panel>
						<textarea
							slot="reference"
							v-model="input"
							ref="areaInput"
							placeholder="请通过@限定范围或直接输入您的问题"
							rows="1"
							class="chat-textarea"
							@input="autoGrow"
							@keyup.enter.prevent="send"
						></textarea>
					</el-popover>
					<!-- <textarea
						v-model="input"
						placeholder="请通过@限定范围或直接输入您的问题"
						rows="1"
						class="chat-textarea"
						@input="autoGrow"
						@keyup.enter.prevent="send"
					></textarea> -->
					<div class="input-actions">
						<div class="actions-left">
							<el-button icon="el-icon-paperclip" plain size="small" disabled></el-button>
							<el-button icon="el-icon-magic-stick" type="primary" plain size="small" disabled>深度思考</el-button>

							<!-- @ -->
							<el-popover placement="bottom" trigger="click">
								<el-cascader-panel
									v-model="selectLibs"
									:options="treeList"
									:props="{ checkStrictly: true, expandTrigger: 'hover', label: 'sname', value: 'id' }"
									@change="handleChangeFw"
								></el-cascader-panel>
								<el-button slot="reference" class="custom-btn" plain round type="primary" size="small">@</el-button>
							</el-popover>
						</div>
						<div class="actions-right">
							<el-button
								class="send-btn"
								type="primary"
								circle
								:disabled="!input.trim() || isAiStreaming || sendDisabled"
								@click="send"
							>
								<i class="el-icon-top"></i>
							</el-button>
						</div>
					</div>
				</div>
				<!-- 猜你想问 -->
				<div class="guess-box">
					<h3>猜你想问</h3>
					<p
						class="cursor-pointer color-blue"
						v-for="(question, qindex) in questionList"
						:key="'ques' + qindex"
						@click="input = question.text"
					>
						{{ question.text }}
					</p>
				</div>
			</div>
			<template v-else>
				<div v-if="activeSession" class="chat-header">
					<h3>{{ activeSession.title }}</h3>
				</div>
				<div class="chat-messages">
					<div
						v-for="(msg, idx) in activeSession.messages"
						:key="idx"
						:class="['chat-message', msg.from === 'user' ? 'from-user' : 'from-ai']"
					>
						<span v-if="msg.from === 'user'" class="user-message">{{ msg.content }}</span>
						<div v-else class="ai-response-container">
							<!-- 加载状态 -->
							<div v-if="msg.isLoading" class="loading-ai">
								<img src="@/assets/images/ai.svg" alt="AI" class="loading-ai-img" />
								<div class="loading-ai-loading">
									<span>思考中</span>
									<i class="el-icon-loading"></i>
								</div>
							</div>
							<div
								class="ai-content markdown-body padding16 padding-bottom16"
								v-else
								v-html="msg.parsed ? msg.parsed.markdownHtml : msg.content"
							></div>
							<div v-if="msg.parsed && msg.parsed.files && msg.parsed.files.length" class="file-tags padding16 padding-bottom16">
								<span class="quote-icon">&ldquo;</span>
								<template v-for="file in msg.parsed.files">
									<el-popover
										v-if="fileManageInfoMap[file.fileId]"
										:key="file.fileId"
										placement="top"
										width="400"
										trigger="hover"
										popper-class="file-details-popover"
									>
										<el-row :gutter="12" class="popover-meta-table">
											<el-col :span="12">
												<span class="meta-label">法律效力阶位：</span>
												<span class="meta-value">{{ formatPopoverValue(fileManageInfoMap[file.fileId].lawRankName) }}</span>
											</el-col>
											<el-col :span="12">
												<span class="meta-label">施行日期：</span>
												<span class="meta-value">{{ formatPopoverValue(fileManageInfoMap[file.fileId].applyDate) }}</span>
											</el-col>
											<el-col :span="12">
												<span class="meta-label">制定机关：</span>
												<span class="meta-value">{{ formatPopoverValue(fileManageInfoMap[file.fileId].issueUnit) }}</span>
											</el-col>
											<el-col :span="12">
												<span class="meta-label">公示日期：</span>
												<span class="meta-value">{{ formatPopoverValue(fileManageInfoMap[file.fileId].releaseDate) }}</span>
											</el-col>
											<el-col :span="12">
												<span class="meta-label">时效性：</span>
												<span class="meta-value">{{ formatPopoverValue(fileManageInfoMap[file.fileId].efficientName) }}</span>
											</el-col>
										</el-row>
										<el-button
											slot="reference"
											type="primary"
											plain
											size="mini"
											@click="onFileClick(file)"
											style="margin-right: 12px; margin-bottom: 8px;"
										>
											<i class="el-icon-document"></i>
											{{ file.fileName.replace(/\.[^.]+$/, '') }}
										</el-button>
									</el-popover>
									<el-button
										v-else
										:key="'_nobutton_' + file.fileId"
										type="primary"
										plain
										size="mini"
										@click="onFileClick(file)"
										style="margin-right: 12px; margin-bottom: 8px;"
									>
										<i class="el-icon-document"></i>
										{{ file.fileName.replace(/\.[^.]+$/, '') }}
									</el-button>
								</template>
							</div>
							<!-- 操作栏 -->
							<div
								v-if="
									msg.parsed &&
										((msg.parsed.files && msg.parsed.files.length) ||
											(msg.parsed.recommends && msg.parsed.recommends.length) ||
											(idx === 0 && historyTips.length))
								"
								class="tool-actions padding16 padding-bottom16"
							>
								<el-button icon="el-icon-copy-document" circle size="small" @click="copyAIContent(msg)"></el-button>
								<el-button
									v-if="idx === activeSession.messages.length - 1"
									icon="el-icon-refresh"
									circle
									size="small"
									@click="refreshAIResponse(idx)"
									:loading="msg.isRefreshing"
									:disabled="msg.isRefreshing"
								></el-button>
								<el-button
									icon="el-icon-download"
									circle
									size="small"
									@click="onDownloadFiles(msg.parsed && msg.parsed.files)"
								></el-button>
							</div>
							<!-- 新增：历史tips推荐 - 只在最后一条AI消息下显示，且只在历史会话下显示 -->
							<div
								v-if="idx === historyTipsMsgIdx && historyTips.length && isHistorySession"
								class="suggested-questions padding16 padding-bottom16"
							>
								<div v-for="(tip, tIdx) in historyTips" :key="'tip-' + tIdx" class="question-item" @click="askSuggestedQuestion(tip)">
									<span>{{ tip }}</span>
									<i class="el-icon-arrow-right"></i>
								</div>
							</div>

							<!-- 原有推荐问题 -->
							<div
								v-if="msg.parsed && msg.parsed.recommends && msg.parsed.recommends.length"
								class="suggested-questions padding16 padding-bottom16"
							>
								<div v-for="q in msg.parsed.recommends" :key="q" class="question-item" @click="askSuggestedQuestion(q)">
									<span>{{ q }}</span>
									<i class="el-icon-arrow-right"></i>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="chat-input">
					<div class="select-tag" v-if="!!selectNames">
						<el-tag closable @close="clearSelectLib">{{ selectNames }}</el-tag>
						<span>（查询限定范围）</span>
					</div>
					<div class="chat-input-box">
						<el-popover placement="top-start" trigger="manual" v-model="inputVisible" popper-class="lib-popover">
							<el-cascader-panel
								v-model="selectLibs"
								:options="treeList"
								:props="{ checkStrictly: true, expandTrigger: 'hover', label: 'sname', value: 'id' }"
								@change="handleChangeFw"
							></el-cascader-panel>
							<textarea
								slot="reference"
								v-model="input"
								ref="areaInput"
								placeholder="请通过@限定范围或直接输入您的问题"
								rows="1"
								class="chat-textarea"
								@input="autoGrow"
								@keyup.enter.prevent="send"
							></textarea>
						</el-popover>
						<!-- <textarea
							v-model="input"
							placeholder="请通过@限定范围或直接输入您的问题"
							rows="1"
							class="chat-textarea"
							@input="autoGrow"
							@keyup.enter.prevent="send"
						></textarea> -->
						<div class="input-actions">
							<div class="actions-left">
								<el-button icon="el-icon-paperclip" plain size="small" disabled></el-button>
								<el-button icon="el-icon-magic-stick" type="primary" plain size="small" disabled>深度思考</el-button>
								<!-- @ -->
								<el-popover placement="bottom" trigger="click">
									<el-cascader-panel
										v-model="selectLibs"
										:options="treeList"
										:props="{ checkStrictly: true, expandTrigger: 'hover', label: 'sname', value: 'id' }"
										@change="handleChangeFw"
									></el-cascader-panel>
									<el-button slot="reference" class="custom-btn" plain round type="primary" size="small">@</el-button>
								</el-popover>
							</div>
							<div class="actions-right">
								<el-button
									class="send-btn"
									type="primary"
									circle
									:disabled="!input.trim() || isAiStreaming || sendDisabled"
									@click="send"
								>
									<i class="el-icon-top"></i>
								</el-button>
							</div>
						</div>
					</div>
				</div>
			</template>
		</div>

		<!-- 文件详情视图 -->
		<div v-else-if="viewMode === 'fileDetails'" class="file-details-view">
			<!-- 头部 -->
			<div class="file-details-header">
				<div class="file-title-wrapper">
					<i class="el-icon-document"></i>
					<span class="link-to-detail" @click="goToKnowledgeDetail">
						{{ activeFileContent ? (activeFileContent.name ? activeFileContent.name.replace(/\.[^.]+$/, '') : '文件详情') : '文件详情' }}
					</span>
				</div>
				<el-button @click="returnToChat" round> <i class="el-icon-back"></i> 返回问答 </el-button>
			</div>
			<!-- 内容 -->
			<div class="file-details-body">
				<!-- 加载状态 -->
				<div v-if="isLoadingFileDetails" class="loading-container">
					<i class="el-icon-loading"></i>
					<span>正在加载文件详情...</span>
				</div>
				<!-- 文件内容 -->
				<div
					v-else-if="activeFileContent && activeFileContent.snippets && activeFileContent.snippets.length > 0"
					class="content-snippet-card"
					v-for="(snippet, index) in activeFileContent.snippets"
					:key="index"
					@click="openInNewTab(snippet, index)"
				>
					<div class="relevance-tag">#{{ index + 1 }} | 相关度 {{ snippet.relevance / 10 }}%</div>
					<div class="snippet-text">{{ snippet.text }}</div>
				</div>
				<!-- 空状态 -->
				<div v-else class="empty-content">
					<i class="el-icon-document"></i>
					<span>暂无文件内容</span>
				</div>
			</div>
		</div>

		<!-- 下载弹窗-->
		<el-dialog
			title="文件下载"
			:visible.sync="showDownloadDialog"
			width="420px"
			:close-on-click-modal="false"
			@close="showDownloadDialog = false"
		>
			<el-table :data="downloadFiles" border style="width: 100%;">
				<el-table-column label="序号" type="index" width="60" align="center"></el-table-column>
				<el-table-column label="文件名称" prop="fileName" align="center"></el-table-column>
				<el-table-column label="操作" width="80" align="center">
					<template slot-scope="scope">
						<el-button icon="el-icon-download" type="text" @click="downloadSingleFile(scope.row)"></el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-dialog>
	</div>
</template>

<script>
// 引入相关API方法
import {
	querySearchHistory, // 查询历史会话列表
	getContentInfo, // 获取文件内容详情
	streamChat, // AI流式对话接口
	querySearchQuAndAn, // 查询历史会话问答及tips
	renameTitle, // 重命名会话
	deleteSearchHistory, // 删除会话
	getManageInfo, // 获取文件管理信息
	getFileUrl, // 获取文件下载地址
	getLibTreeForAISearch, // 获取文库级联
	getFileIdsForType // 获取选中文库对应的文件id
} from 'api/modules/knowledgebaseai'
import { mapState } from 'vuex'
import MarkdownIt from 'markdown-it'
import VUE_CONFIG from '../../../../vue.config'

export default {
	name: 'KnowledgeBaseAI',
	// 自定义指令：用于输入框在显示时自动获得焦点
	directives: {
		focus: {
			inserted: function(el) {
				el.focus()
			}
		}
	},
	data() {
		return {
			// --- UI状态 ---
			isCollapsed: false, // 侧边栏是否折叠
			viewMode: 'chat', // 当前视图模式: 'chat' 聊天 或 'fileDetails' 文件详情

			// --- 会话数据 ---
			sessions: [], // 所有会话对象数组
			activeSessionId: null, // 当前激活的会话ID，null表示欢迎界面
			renamingSessionId: null, // 正在重命名的会话ID

			// --- 数据内容 ---
			input: '', // 用户输入框内容
			isDeepThinkEnabled: true, // "深度思考"模式开关（预留）
			activeFileContent: null, // 文件详情视图下当前显示的文件内容
			isLoadingFileDetails: false, // 文件详情加载状态
			md: null, // markdown-it 实例
			showDownloadDialog: false, // 是否显示下载弹窗
			downloadFiles: [], // 下载文件列表
			historyTips: [], // 历史tips推荐问题
			historyTipsSessionId: null, // tips所属会话ID
			historyTipsMsgIdx: null, // tips显示的AI消息下标
			isAiStreaming: false, // AI流式处理中，控制发送按钮禁用
			fileManageInfoMap: {}, // 存储 fileId => 文件信息对象

			// --- @级联 ---
			treeList: [],
			selectLibs: [],
			selectNames: '',
			aIndex: -1, // 手动输入时@的位置
			inputVisible: false,
			sendDisabled: false,
			fileIds: [],
			questionList: [
				{ text: '招标文件编制过程中对于招标人有哪些规定，涉及到哪些法律条文，具体规定是什么' },
				{ text: '在招投标过程中，如何利用法律知识库确保合规性？' },
				{ text: '提供一些常见的招投标法律问题及解决办法' }
			]
		}
	},
	computed: {
		/**
		 * @description: 根据 activeSessionId 找到当前激活的会话对象
		 * @return {Object | undefined} 返回会话对象或 undefined
		 */
		activeSession() {
			return this.sessions.find((s) => s.id === this.activeSessionId)
		},
		...mapState('user', {
			userInfo: (state) => state.userInfo
		}),
		// 计算历史tips显示在哪条AI消息下（最后一条AI消息）
		lastAiMsgIdx() {
			const session = this.activeSession
			if (!session || !session.messages) return -1
			let lastIdx = -1
			session.messages.forEach((msg, idx) => {
				if (msg.from === 'ai') lastIdx = idx
			})
			return lastIdx
		},
		// 判断当前会话是否为历史会话（只有handleSessionSelect赋值过historyTips才算历史会话）
		isHistorySession() {
			return this.historyTips && this.historyTips.length > 0 && this.activeSessionId === this.historyTipsSessionId
		},
		// 上传接口地址（根据环境变量和代理配置自动切换）
		uploadUrl() {
			let _uploadUrl = ''
			if (process.env.VUE_APP_UPLOAD_URL == '/uploadApi') {
				_uploadUrl = VUE_CONFIG.devServer.proxy['/uploadApi'].target
			} else {
				_uploadUrl = process.env.VUE_APP_UPLOAD_URL
			}
			return _uploadUrl
		},
		transformList() {
			return this.$utils.toTreeArray(this.treeList)
		}
	},
	methods: {
		/**
		 * @description: 聊天消息区滚动到底部，确保最新消息可见
		 */
		scrollToBottom() {
			// 使用 $nextTick 确保在DOM更新完成后执行滚动操作
			this.$nextTick(() => {
				const messagesContainer = this.$el.querySelector('.chat-messages')
				if (messagesContainer) {
					messagesContainer.scrollTop = messagesContainer.scrollHeight
				}
			})
		},
		/**
		 * @description: 切换到文件详情视图，准备加载文件内容
		 * @param {Object} file 被点击的文件对象
		 */
		showFileDetails(file) {
			// 清空之前的文件内容，准备加载新的内容
			this.activeFileContent = null
			this.viewMode = 'fileDetails'
		},
		/**
		 * @description: 从文件详情视图返回到聊天视图
		 */
		returnToChat() {
			this.viewMode = 'chat'
			this.activeFileContent = null
		},
		/**
		 * @description: 根据文本域内容自动调整其高度（自适应输入框）
		 * @param {Event} event - input事件对象
		 */
		autoGrow(event) {
			// console.log('😍 ~ event:', event)
			if (event.data && event.data == '@') {
				// 记录此时@的索引:光标位置 - 1（因为输入后光标在@的下一位）
				this.aIndex = event.target.selectionStart - 1
				// 打开弹窗
				this.inputVisible = true
			}
			const el = event.target
			el.style.height = 'auto'
			el.style.height = el.scrollHeight + 'px'
		},
		/**
		 * @description: 切换"深度思考"模式的开关状态（预留功能）
		 */
		toggleDeepThink() {
			this.isDeepThinkEnabled = !this.isDeepThinkEnabled
		},
		/**
		 * @description: 切换侧边栏的展开/折叠状态
		 */
		toggleCollapse() {
			this.isCollapsed = !this.isCollapsed
		},
		/**
		 * @description: 点击历史会话，切换到该会话并加载历史消息和tips
		 * @param {string} id - 被点击会话的ID
		 */
		handleSessionSelect(id) {
			// 切换历史会话时关闭文件详情视图
			this.viewMode = 'chat'
			this.activeFileContent = null
			this.activeSessionId = id
			this.scrollToBottom()
			querySearchQuAndAn({ searchId: id, userId: this.userInfo.id }).then((res) => {
				// 新增：处理tips
				if (res && res.data && Array.isArray(res.data.tips)) {
					this.historyTips = res.data.tips.map((tip) => tip.tips).filter((tip) => tip)
					this.historyTipsSessionId = id // 记录当前tips属于哪个会话
				} else {
					this.historyTips = []
					this.historyTipsSessionId = null
				}

				if (res && res.data && Array.isArray(res.data.objectList) && res.data.objectList.length > 0) {
					// 新格式：处理objectList中的searchList
					let mergedMessages = []

					res.data.objectList.forEach((obj) => {
						if (obj.searchList && Array.isArray(obj.searchList)) {
							obj.searchList.forEach((item) => {
								// 添加用户问题
								if (item.question) {
									mergedMessages.push({
										from: 'user',
										content: item.question.content,
										isTyping: false,
										isLoading: false
									})
								}

								// 添加AI回答
								if (item.answer) {
									let content = item.answer.content
									// 处理content中的数组格式
									if (typeof content === 'string') {
										try {
											const arr = JSON.parse(content)
											if (Array.isArray(arr)) {
												content = arr.join('')
											}
										} catch {}
									}

									const parsed = this.parseAIContent(content)
									// 如果有files信息，添加到parsed中
									if (obj.files && Array.isArray(obj.files)) {
										obj.files.forEach((file) => {
											if (file.groupId === item.groupId) parsed.files.push(file)
										})
										// parsed.files = obj.files;
									}

									mergedMessages.push({
										from: 'ai',
										content,
										parsed,
										isTyping: false,
										isLoading: false
									})
								}
							})
						}
					})

					// 新增：合并 manageInfo 到 fileManageInfoMap
					res.data.objectList.forEach((obj) => {
						if (Array.isArray(obj.manageInfo)) {
							const map = {}
							obj.manageInfo.forEach((item) => {
								if (item.fileId) map[item.fileId] = item
							})
							this.fileManageInfoMap = { ...this.fileManageInfoMap, ...map }
						}
					})

					// 赋值到当前会话
					const session = this.sessions.find((s) => s.id === id)
					if (session) {
						session.messages = mergedMessages
						// 新增：记录历史AI消息的最后一条下标
						let lastIdx = -1
						mergedMessages.forEach((msg, idx) => {
							if (msg.from === 'ai') lastIdx = idx
						})
						this.historyTipsMsgIdx = lastIdx
					}
					this.scrollToBottom()
				} else {
					this.historyTipsMsgIdx = null
				}
			})
		},
		/**
		 * @description: 发送消息，创建新会话或追加到当前会话，并调用AI流式接口
		 */
		send() {
			if (this.isAiStreaming) return // AI流式处理中禁止重复发送
			const userMessage = this.input.trim() // 去除多余空格和换行
			if (!userMessage) return // 防止发送空消息

			let currentSession = this.activeSession

			// 如果当前没有激活的会话, 则创建一个新会话
			if (!currentSession) {
				// 生成唯一searchId
				const newSearchId = Date.now().toString() + Math.floor(Math.random() * 10000).toString()
				currentSession = {
					id: newSearchId,
					title: userMessage.substring(0, 20),
					messages: []
				}
				this.sessions.unshift(currentSession)
				this.activeSessionId = newSearchId
				this.historyTips = [] // 新会话清空tips
				this.historyTipsSessionId = null // 新会话清空tips所属会话
			}

			// 添加用户消息
			currentSession.messages.push({ from: 'user', content: userMessage })

			// 添加一个空的AI消息，用于接收流式数据
			// isTyping复用模板中原有的逻辑，用于控制消息底部(如文件、推荐问题)的显示
			currentSession.messages.push({
				from: 'ai',
				content: '',
				isTyping: true,
				isLoading: true, // AI消息加载中
				files: [],
				suggestsQuestions: []
			})

			this.input = '' // 清空输入框
			this.scrollToBottom()

			// 调用后端流式接口
			this.callStreamApi()
		},
		/**
		 * @description: 解析AI回复内容，提取<button>和<recommend>标签内容为结构化数据
		 * @param {string} raw - AI原始回复内容
		 * @return {Object} 结构化内容（文件、推荐问题、markdown、复制内容）
		 */
		parseAIContent(raw) {
			if (!raw) return { files: [], recommends: [], markdownHtml: '', copyContent: '' }
			// 不处理 <think>，直接保留原文
			// 提取<button>...</button>内容
			let files = []
			raw = raw.replace(/<button>([\s\S]*?)<\/button>/, (match, p1) => {
				try {
					files = JSON.parse(p1)
				} catch {
					try {
						let fixed = p1.replace(/'/g, '"').replace(/([{,])\s*([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
						files = JSON.parse(fixed)
					} catch {}
				}
				return ''
			})
			// 提取<recommend>...</recommend>内容
			let recommends = []
			raw = raw.replace(/<recommend>([\s\S]*?)<\/recommend>/, (match, p1) => {
				try {
					recommends = JSON.parse(p1)
				} catch {
					try {
						let fixed = p1.replace(/'/g, '"').replace(/([{,])\s*([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
						recommends = JSON.parse(fixed)
					} catch {}
				}
				return ''
			})
			// 渲染 markdown
			let markdownSource = raw
			let markdownHtml = this.md ? this.md.render(markdownSource) : markdownSource
			return { files, recommends, markdownHtml, copyContent: markdownSource }
		},
		/**
		 * @description: 复制AI回答的内容到剪贴板
		 * @param {Object} msg - 消息对象
		 */
		copyAIContent(msg) {
			const textToCopy = msg.parsed?.copyContent
			if (!textToCopy) {
				this.$message.warning('没有可复制的内容')
				return
			}
			if (navigator.clipboard) {
				navigator.clipboard
					.writeText(textToCopy)
					.then(() => {
						this.$message.success('复制成功')
					})
					.catch((err) => {
						this.$message.error('复制失败，请稍后重试')
					})
			} else {
				// 兼容处理
				const textArea = document.createElement('textarea')
				textArea.value = textToCopy
				textArea.style.position = 'fixed'
				document.body.appendChild(textArea)
				textArea.focus()
				textArea.select()
				try {
					document.execCommand('copy')
					this.$message.success('复制成功')
				} catch (err) {
					this.$message.error('复制失败')
				}
				document.body.removeChild(textArea)
			}
		},
		/**
		 * @description: 处理AI回复的文件信息（每次AI回复后都请求）
		 * @param {Object} aiMessage - AI消息对象
		 */
		handleNewSessionFileInfo(aiMessage) {
			// 只要AI消息有文件引用就请求
			const parsed = aiMessage.parsed
			if (parsed && parsed.files && parsed.files.length > 0) {
				// fileIdList 必须取自 button 标签中的 fileId 字段（不是 id 字段）
				const fileIdList = parsed.files.map((file) => file.fileId).filter((fileId) => fileId)

				if (fileIdList.length > 0) {
					getManageInfo({ fileIdList })
						.then((res) => {
							if (Array.isArray(res.data)) {
								// 转为 fileId => 文件信息对象
								const map = {}
								res.data.forEach((item) => {
									if (item.fileId) map[item.fileId] = item
								})
								this.fileManageInfoMap = { ...this.fileManageInfoMap, ...map }
							}
						})
						.catch((error) => {
							console.error('获取文件管理信息失败:', error)
						})
				}
			}
		},
		/**
		 * @description: 调用后端流式接口，获取AI回复（新消息）
		 */
		callStreamApi() {
			const currentSession = this.activeSession
			if (!currentSession) return

			// 获取最后一条AI消息用于填充内容
			const aiMessage = currentSession.messages[currentSession.messages.length - 1]
			// 获取用户刚刚发送的消息
			const userMessageContent = currentSession.messages[currentSession.messages.length - 2].content

			const requestData = {
				message: userMessageContent,
				conversation_id: currentSession.id, // 用searchId替换newId
				createUserId: this.userInfo.id,
				createUser: this.userInfo.sname,
				fileIds: this.fileIds
			}

			this.isAiStreaming = true // 开始流式，禁用发送按钮
			streamChat(requestData)
				.then((response) => {
					aiMessage.isLoading = false // 流式开始，隐藏加载图标
					if (!response.ok) {
						this.isAiStreaming = false
						throw new Error(`网络响应错误, 状态码: ${response.status}`)
					}
					const reader = response.body.getReader()
					const decoder = new TextDecoder('utf-8')

					let buffer = '' // 新增：缓存未处理的内容

					const read = () => {
						reader
							.read()
							.then(({ done, value }) => {
								if (done) {
									this.$set(aiMessage, 'parsed', this.parseAIContent(aiMessage.content))
									aiMessage.isTyping = false // 流结束，显示底部内容
									this.scrollToBottom()

									// 每次AI回复流式输出结束后处理文件信息
									this.handleNewSessionFileInfo(aiMessage)

									this.isAiStreaming = false // 流式结束，恢复发送按钮
									console.log('流式处理结束')
									return
								}
								const chunk = decoder.decode(value, { stream: true })
								const lines = chunk.split(/\r?\n/)
								let clean = ''
								let paragraph = ''
								for (let line of lines) {
									if (line.startsWith('data:')) {
										let content = line.replace(/^data:\s*/, '')
										if (content) {
											paragraph += content
										} else {
											// 空data:，表示段落结束，换行
											if (paragraph) {
												clean += paragraph + '\n'
												paragraph = ''
											} else {
												clean += '\n'
											}
										}
									}
								}
								// 拼接最后一段
								if (paragraph) clean += paragraph
								buffer += clean
								// 直接拼接所有内容，不处理think标签
								aiMessage.content += buffer
								buffer = ''
								// 实时解析内容，实时渲染结构化样式
								this.$set(aiMessage, 'parsed', this.parseAIContent(aiMessage.content))
								this.scrollToBottom() // 实时滚动
								read()
							})
							.catch((error) => {
								aiMessage.isLoading = false // 请求失败，隐藏加载图标
								aiMessage.content = '读取数据流时发生错误。'
								aiMessage.isTyping = false
								this.isAiStreaming = false // 出错也恢复发送按钮
							})
					}

					read()
				})
				.catch((error) => {
					aiMessage.isLoading = false // 请求失败，隐藏加载图标
					console.error('请求流式API失败:', error)
					aiMessage.content = '抱歉，服务暂时无法连接，请稍后再试。'
					aiMessage.isTyping = false
					this.isAiStreaming = false // 出错也恢复发送按钮
				})
		},
		/**
		 * @description: 点击推荐问题，将其内容填充到输入框
		 * @param {string} question - 被点击的问题文本
		 */
		askSuggestedQuestion(question) {
			this.input = question
		},
		/**
		 * @description: 点击"新建对话"按钮，切换到欢迎界面，等待用户输入
		 */
		createSession() {
			// 切换回聊天视图，以确保在任何视图下都能正确响应
			this.viewMode = 'chat'
			// 将 activeSessionId 设为 null 以显示欢迎界面
			// 直到用户发送第一条消息时，才会真正创建新会话
			this.activeSessionId = null
		},
		/**
		 * @description: 处理历史记录项的下拉菜单命令（重命名/删除）
		 * @param {Object} command - 包含动作和数据的命令对象
		 */
		handleCommand(command) {
			if (command.action === 'rename') {
				this.startRename(command.session)
			} else if (command.action === 'delete') {
				this.deleteSession(command.id)
			}
		},
		/**
		 * @description: 开始重命名会话，显示输入框
		 * @param {Object} session - 要重命名的会话对象
		 */
		startRename(session) {
			this.renamingSessionId = session.id
		},
		/**
		 * @description: 完成重命名，调用接口并关闭输入框
		 */
		finishRename() {
			// 调用重命名接口
			const session = this.sessions.find((s) => s.id === this.renamingSessionId)
			if (session) {
				renameTitle({ id: session.id, title: session.title })
					.then(() => {
						this.$message.success('重命名成功')
					})
					.catch(() => {
						this.$message.error('重命名失败')
					})
			}
			this.renamingSessionId = null
		},
		/**
		 * @description: 删除会话，调用接口并刷新历史对话
		 * @param {string} id - 要删除的会话ID
		 */
		deleteSession(id) {
			// 只调用接口，成功后本地移除会话
			deleteSearchHistory({ id })
				.then(() => {
					this.$message.success('删除成功')
					// 本地移除会话
					this.sessions = this.sessions.filter((s) => s.id !== id)
					// 如果当前激活会话被删除，重置activeSessionId
					if (this.activeSessionId === id) {
						this.activeSessionId = null
					}
				})
				.catch(() => {
					this.$message.error('删除失败')
				})
		},
		/**
		 * @description: 打开新标签页，显示相关度标签的详细信息
		 * @param {Object} snippet - 被点击的相关度标签对象
		 * @param {Number} index - 被点击的相关度标签的索引
		 */
		openInNewTab(snippet, index) {
			console.log(snippet)

			// 检查必要的数据是否存在
			if (!snippet || !snippet.fileId) {
				this.$message.warning('数据不完整，无法打开详情页')
				return
			}

			// 只传递文件主键id
			const data = { fileId: snippet.fileId }
			const encodedData = encodeURIComponent(JSON.stringify(data))
			const newTabUrl = `${window.location.origin}${window.location.pathname}#/knowledge-detail?data=${encodedData}`
			window.open(newTabUrl, '_blank')
			this.$message.success('已在新标签页中打开详细信息')
		},
		/**
		 * @description: 文件按钮点击事件，显示文件详情
		 */
		onFileClick(file) {
			// 显示文件详情视图并请求文件内容
			this.showFileDetails(file)
			// 传递 fileId 和文件名
			this.getFileContentInfo(file.id, file.fileName || file.name || '未知文件')
		},
		/**
		 * @description: 请求文件内容信息，更新activeFileContent
		 * @param {string} fileId - 文件ID
		 * @param {string} fileName - 文件名称
		 */
		getFileContentInfo(fileId, fileName) {
			this.isLoadingFileDetails = true
			getContentInfo({ fileId })
				.then((response) => {
					this.activeFileContent = {
						fileId: fileId,
						name: fileName, // 直接用传入的文件名
						snippets: Array.isArray(response.data)
							? response.data.map((item) => ({
									relevance: item.similarity ? (parseFloat(item.similarity) * 100).toFixed(2) : '--',
									text: item.content || '',
									fileId: item.fileId // 补充 fileId 到 snippet
							  }))
							: [],
						details: {}
					}
				})
				.catch((error) => {
					console.error('请求文件详情失败:', error)
					this.$message.error('获取文件详情失败，请稍后重试')
				})
				.finally(() => {
					this.isLoadingFileDetails = false
				})
		},
		/**
		 * @description: 刷新AI响应，重新生成AI回复
		 * @param {Number} index - AI消息索引
		 */
		refreshAIResponse(index) {
			const currentSession = this.activeSession
			if (!currentSession || !currentSession.messages[index]) {
				this.$message.warning('无法刷新此消息')
				return
			}

			// 获取AI消息
			const aiMessage = currentSession.messages[index]

			// 检查是否是AI消息
			if (aiMessage.from !== 'ai') {
				this.$message.warning('只能刷新AI回复')
				return
			}

			// 获取对应的用户消息（AI消息的前一条）
			const userMessageIndex = index - 1
			const userMessage = currentSession.messages[userMessageIndex]

			if (!userMessage || userMessage.from !== 'user') {
				this.$message.warning('找不到对应的用户消息')
				return
			}

			// 添加刷新状态
			this.$set(aiMessage, 'isRefreshing', true)

			// 清空AI消息内容，准备重新生成
			aiMessage.content = ''
			aiMessage.parsed = null
			aiMessage.isLoading = true
			aiMessage.isTyping = true

			// 重新调用流式API
			this.callStreamApiForRefresh(userMessage.content, currentSession.id, aiMessage)
		},
		/**
		 * @description: 为刷新功能调用后端流式接口，更新指定AI消息
		 * @param {string} userMessageContent - 用户消息内容
		 * @param {string} conversationId - 会话ID
		 * @param {Object} aiMessage - AI消息对象
		 */
		callStreamApiForRefresh(userMessageContent, conversationId, aiMessage) {
			const requestData = {
				message: userMessageContent,
				conversation_id: conversationId, // 用searchId替换newId
				createUserId: this.userInfo.id,
				createUser: this.userInfo.sname,
				fileIds: this.fileIds
			}

			this.isAiStreaming = true // 开始流式，禁用发送按钮
			streamChat(requestData)
				.then((response) => {
					aiMessage.isLoading = false // 流式开始，隐藏加载图标
					if (!response.ok) {
						this.isAiStreaming = false
						throw new Error(`网络响应错误, 状态码: ${response.status}`)
					}
					const reader = response.body.getReader()
					const decoder = new TextDecoder('utf-8')

					let buffer = '' // 新增：缓存未处理的内容

					const read = () => {
						reader
							.read()
							.then(({ done, value }) => {
								if (done) {
									this.$set(aiMessage, 'parsed', this.parseAIContent(aiMessage.content))
									aiMessage.isTyping = false // 流结束，显示底部内容
									aiMessage.isRefreshing = false // 刷新完成
									this.scrollToBottom()
									this.isAiStreaming = false // 流式结束，恢复发送按钮
									return
								}
								const chunk = decoder.decode(value, { stream: true })
								const lines = chunk.split(/\r?\n/)
								let clean = ''
								let paragraph = ''
								for (let line of lines) {
									if (line.startsWith('data:')) {
										let content = line.replace(/^data:\s*/, '')
										if (content) {
											paragraph += content
										} else {
											// 空data:，表示段落结束，换行
											if (paragraph) {
												clean += paragraph + '\n'
												paragraph = ''
											} else {
												clean += '\n'
											}
										}
									}
								}
								// 拼接最后一段
								if (paragraph) clean += paragraph
								buffer += clean
								// 直接拼接所有内容，不处理think标签
								aiMessage.content += buffer
								buffer = ''
								// 实时解析内容，实时渲染结构化样式
								this.$set(aiMessage, 'parsed', this.parseAIContent(aiMessage.content))
								this.scrollToBottom() // 实时滚动
								read()
							})
							.catch((error) => {
								aiMessage.isLoading = false // 请求失败，隐藏加载图标
								aiMessage.isRefreshing = false // 刷新失败
								aiMessage.content = '读取数据流时发生错误。'
								aiMessage.isTyping = false
								this.isAiStreaming = false // 出错也恢复发送按钮
							})
					}

					read()
				})
				.catch((error) => {
					aiMessage.isLoading = false // 请求失败，隐藏加载图标
					aiMessage.isRefreshing = false // 刷新失败
					console.error('请求流式API失败:', error)
					aiMessage.content = '抱歉，服务暂时无法连接，请稍后再试。'
					aiMessage.isTyping = false
					this.isAiStreaming = false // 出错也恢复发送按钮
				})
		},
		/**
		 * @description: 跳转到知识详情页面（新标签页）
		 */
		goToKnowledgeDetail() {
			// 检查是否有文件内容
			if (!this.activeFileContent) {
				this.$message.warning('暂无文件信息')
				return
			}

			// 获取当前文件的ID（从activeFileContent中获取）
			// 这里需要根据实际的数据结构来获取fileId
			// 假设fileId存储在activeFileContent.fileId中
			const fileId = this.activeFileContent.fileId

			if (!fileId) {
				this.$message.warning('文件ID不存在')
				return
			}

			// 构建详情页URL
			const detailUrl = `${window.location.origin}${window.location.pathname}#/knowledge-detail?fileId=${fileId}`

			// 在新标签页中打开详情页
			window.open(detailUrl, '_blank')

			// 显示提示信息
			this.$message.success('已在新标签页中打开文件详情')
		},
		/**
		 * @description: 查询历史对话列表，刷新sessions
		 * @param {boolean} keepActiveId - 是否保持当前激活会话
		 */
		queryHistory(keepActiveId) {
			querySearchHistory({
				userId: this.userInfo.id
			})
				.then((res) => {
					if (res && Array.isArray(res.data)) {
						this.sessions = res.data.map((item) => ({
							id: item.id,
							title: item.title,
							createUser: item.createUser,
							createDate: item.createDate,
							messages: []
						}))
						// 保持重命名前的会话为激活状态，并自动加载历史消息
						if (keepActiveId && this.sessions.some((s) => s.id === keepActiveId)) {
							this.activeSessionId = keepActiveId
							this.$nextTick(() => {
								this.handleSessionSelect(keepActiveId)
							})
						}
					} else {
						this.sessions = []
						this.activeSessionId = null
					}
				})
				.catch(() => {
					this.sessions = []
					this.activeSessionId = null
				})
		},
		/**
		 * @description: 下载AI引用的文件，弹出下载列表
		 * @param {Array} files - 文件对象数组
		 */
		onDownloadFiles(files) {
			if (!files || !files.length) {
				this.$message.warning('没有可下载的文件')
				return
			}
			const fileIds = files.map((f) => f.fileId).filter(Boolean)
			if (!fileIds.length) {
				this.$message.warning('文件ID不存在，无法下载')
				return
			}
			getFileUrl({ fileIds })
				.then((res) => {
					if (res && res.data && Array.isArray(res.data)) {
						this.downloadFiles = res.data
						this.showDownloadDialog = true
					} else {
						this.$message.error('获取下载列表失败')
					}
				})
				.catch(() => {
					this.$message.error('下载失败，请稍后重试')
				})
		},
		/**
		 * @description: 下载单个文件
		 * @param {Object} file - 文件对象
		 */
		downloadSingleFile(file) {
			window.open(this.uploadUrl + file.fileUrl + '?name=' + file.fileName)
		},
		/**
		 * @description: 弹窗字段值格式化，null/undefined/空串/'null'都显示为--
		 */
		formatPopoverValue(val) {
			if (val === null || val === undefined || val === '' || val === 'null') {
				return '--'
			}
			return val
		},

		/**
		 * @description：获取@下展示的范围
		 */
		getTreeList() {
			getLibTreeForAISearch({})
				.then((res) => {
					if (res.data && res.data[0]) {
						this.treeList = res.data[0].children
					}
				})
				.catch((err) => {
					this.treeList = []
				})
		},
		/**
		 * @description：切换@下展示的范围
		 */
		async handleChangeFw(value) {
			let snameArr = []
			value.map((x) => {
				let item = this.transformList.find((y) => y.id == x)
				!!item ? snameArr.push(item.sname) : ''
			})

			if (snameArr.length > 0) {
				this.selectNames = snameArr.join(' > ')
			}

			if (this.inputVisible) {
				this.inputVisible = false
			}

			if (!value || value.length == 0) {
				return
			}

			// 选中后，调接口获取filelds
			try {
				// 暂时禁止发送按钮，等待接口返回
				this.sendDisabled = true
				let res = await getFileIdsForType({ typeId: value[value.length - 1] })
				// 设置
				this.fileIds = res.data
				// 如果是手动输入的@，精准删除 截取@之前的内容 + @之后的内容
				let _index = this.aIndex
				if (_index > -1 && this.input[_index] == '@') {
					this.input = this.input.slice(0, _index) + this.input.slice(_index + 1)
					// 清空aIndex
					this.aIndex = -1
				}
			} catch (err) {
			} finally {
				setTimeout(() => {
					this.sendDisabled = false
				}, 1000)
			}
		},
		/**
		 * @description：清除@选中的范围
		 */
		clearSelectLib() {
			this.selectLibs = []
			this.selectNames = ''
			this.fileIds = []
		}
	},
	created() {
		// 初始化：打印上传地址，查询历史会话，初始化markdown渲染器
		this.queryHistory()
		this.md = new MarkdownIt()

		// 获取文库级联
		this.getTreeList()
	},
	mounted() {
		let that = this
		window.addEventListener('click', (e) => {
			if (that.inputVisible) {
				that.inputVisible = false
			}
		})

		// 初始聚焦
		this.$nextTick(() => {
			this.$refs.areaInput ? this.$refs.areaInput.focus() : ''
		})
	},
	watch: {
		activeSession: {
			handler: function(val) {
				if (val) {
					this.$nextTick(() => {
						this.$refs.areaInput ? this.$refs.areaInput.focus() : ''
					})
				}
			},
			deep: true
		}
	}
}
</script>
<style scoped lang="less">
// ===================
// 智能知识库AI主界面样式
// ===================
.knowledge-base-ai {
	display: flex;
	height: 100%;
	background: #f5f6fa;
	position: relative;
}
// 侧边栏样式
.sidebar {
	background: #fff;
	border-right: 1px solid #ebeef5;
	display: flex;
	flex-direction: column;
	width: 300px;
	min-width: 60px;
	transition: width 0.2s;
	&.collapsed {
		width: 60px;
		.sidebar-title,
		.sidebar-new-session,
		.sidebar-history-title {
			display: none;
		}
	}
	// 侧边栏头部
	.sidebar-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px;
		.sidebar-title {
			font-weight: bold;
			font-size: 20px;
			color: #66b1ff;
		}
		.collapse-btn {
			font-size: 25px;
			cursor: pointer;
			transition: color 0.2s;
			&:hover {
				color: #66b1ff;
			}
		}
	}
	// 新建会话按钮
	.sidebar-new-session {
		padding: 0 16px 12px 16px;
		.new-session-btn {
			width: 100%;
			background: #409eff;
			color: #fff;
			border: none;
			border-radius: 4px;
			padding: 6px 0;
			font-size: 14px;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 4px;
			transition: background 0.2s;
		}
		.new-session-btn:hover {
			background: #66b1ff;
		}
	}
	// 历史会话标题
	.sidebar-history-title {
		padding: 0 16px 8px 16px;
		font-size: 13px;
		color: #888;
		font-weight: bold;
		letter-spacing: 1px;
		display: flex;
		align-items: center;
		gap: 6px;
	}
	// 会话列表
	.session-list {
		flex: 1;
		overflow-y: auto;
		list-style: none;
		margin: 0;
		padding: 0 8px 12px 8px;
		.session-item {
			padding: 10px 8px;
			cursor: pointer;
			transition: background 0.2s;
			font-size: 14px;
			color: #333;
			border-radius: 4px;
			display: flex;
			align-items: center;
			justify-content: space-between;

			&.active {
				background: #e6f7ff;
				.session-title {
					color: #409eff;
					font-weight: bold;
				}
			}

			&:hover {
				background: #f5faff;
				.actions-dropdown {
					opacity: 1;
				}
			}

			.session-title {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				flex: 1;
				min-width: 0;
				padding-right: 8px;
			}

			.rename-input {
				width: 100%;
				border: 1px solid #409eff;
				border-radius: 4px;
				padding: 4px 8px;
				font-size: 14px;
				outline: none;
			}

			.actions-dropdown {
				opacity: 0;
				transition: opacity 0.2s;
				.actions-trigger {
					color: #606266;
					transform: rotate(90deg);
					display: inline-block;
					cursor: pointer;
					padding: 2px;
					&:hover {
						color: #409eff;
					}
				}
			}
		}
	}
}
// 聊天主内容区
.chat-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	position: relative;
	min-height: 0;

	// 欢迎界面居中
	.welcome-center {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 0;
		// padding-bottom: 10vh; /* 将内容向上推离中心 */
		.welcome {
			margin-bottom: 32px;
			h2 {
				font-size: 2rem;
				color: #222;
				font-weight: bold;
			}
		}
		.welcome-input {
			width: 90%;
			display: flex;
			flex-direction: column;
			background: #fff;
			border-radius: 16px; /* 圆角加大 */
			border: none;
			box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1); /* 阴影更明显 */
			padding: 32px 32px 20px 32px; /* 上下左右内边距加大 */
			margin: 0 auto;
			align-items: stretch;
			.chat-textarea {
				border: none;
				outline: none;
				resize: none;
				width: 100%;
				font-size: 14px;
				line-height: 1.6;
				background: transparent;
				font-family: inherit;
				max-height: 100px; /* 限制最大高度 */
				min-height: 80px; /* 将最小高度调整为100px */
				overflow-y: auto;
				&::placeholder {
					color: #999;
				}
			}

			.input-actions {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-top: 8px;

				.actions-left {
					display: flex;
					align-items: center;
					gap: 0;
					// 优化按钮间距
					.el-button + .el-button {
						margin-left: 5px;
					}
				}

				.action-btn {
					background: #fff;
					border: 1px solid #dcdfe6;
					border-radius: 4px;
					padding: 4px 10px;
					cursor: pointer;
					display: flex;
					align-items: center;
					gap: 6px;
					font-size: 13px;
					color: #606266;
					transition: all 0.2s;

					&:hover {
						border-color: #409eff;
						color: #409eff;
					}

					&.deep-think-btn {
						border-color: #d9ecff;
						color: #409eff;
						background-color: #ecf5ff;
						&:hover,
						&.is-active {
							background-color: #409eff;
							color: #fff;
							border-color: #409eff;
						}
					}

					i {
						font-size: 14px;
					}
				}

				.send-btn {
					width: 40px;
					height: 40px;
					border-radius: 50%;
					border: none;
					cursor: pointer;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 16px;
					background: #e0e0e0;
					color: #fff;
					transition: background-color 0.2s;

					&:disabled {
						cursor: not-allowed;
					}

					&:not(:disabled) {
						background: #409eff;
					}
				}
			}
		}
	}

	// 聊天顶部会话标题
	.chat-header {
		padding: 16px;
		border-bottom: 1px solid #ebeef5;
		background: #fff;
	}
	// 聊天消息区
	.chat-messages {
		flex: 1;
		padding: 16px;
		overflow-y: auto;
		background: #f9f9f9;
		display: flex;
		flex-direction: column;
		.chat-message {
			margin-bottom: 12px;
			// padding: 14px 20px;
			border-radius: 6px;
			max-width: 90%;
			word-break: break-all;
			display: flex;
			// 用户消息气泡
			&.from-user {
				background: #e6f7ff;
				align-self: flex-end;
				justify-content: flex-end;
				max-width: 85%;
				font-size: 16px;
			}
			// AI消息气泡
			&.from-ai {
				background: #fff;
				align-self: flex-start;
				justify-content: flex-start;
				border: 1px solid #e0e0e0;
				// padding: 16px;
			}
			.user-message {
				padding: 16px;
			}
		}
	}
	// 聊天输入区
	.chat-input {
		width: 100%;
		background: #fff;
		border: none;
		padding: 24px 32px 20px 32px;
		.chat-input-box {
			display: flex;
			flex-direction: column;
			align-items: stretch;

			background-color: #f3f4f6;
			border-radius: 12px;
			padding: 10px 8px;
			.chat-textarea {
				border: none;
				outline: none;
				resize: none;
				width: 100%;
				font-size: 18px;
				line-height: 1.8;
				background-color: #f3f4f6;
				box-shadow: none;
				min-height: 80px;
				max-height: 100px;
				font-family: inherit;
				&::placeholder {
					color: #bbb;
					font-size: 16px;
				}
			}
			.input-actions {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;
				margin-top: 0;
				.actions-left {
					display: flex;
					align-items: center;
					gap: 0;
					// 优化按钮间距
					.el-button + .el-button {
						margin-left: 2px;
					}
				}
				.action-btn {
					background: #409eff;
					border: none;
					border-radius: 8px;
					padding: 8px 18px;
					cursor: pointer;
					display: flex;
					align-items: center;
					gap: 8px;
					font-size: 15px;
					color: #fff;
					font-weight: 500;
					box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
					transition: all 0.2s;
					&:hover {
						background: #66b1ff;
					}
					i {
						font-size: 18px;
					}
				}
				.send-btn {
					width: 40px;
					height: 40px;
					border-radius: 50%;
					border: none;
					cursor: pointer;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 20px;
					background: #409eff;
					color: #fff;
					margin-left: 12px;
					transition: background-color 0.2s;
					&:disabled {
						cursor: not-allowed;
						background: #e0e0e0;
					}
					&:not(:disabled) {
						background: #409eff;
					}
				}
			}
		}
	}
}
// AI回复区样式
.ai-response-container {
	display: flex;
	flex-direction: column;
	width: 100%;
	.ai-content {
		background: #fff;
		font-size: 15px;
		color: #222;
		line-height: 1.8;
		word-break: break-word;
		margin-top: 16px;
	}
	.loading-ai {
		display: flex;
		align-items: center;
		color: #409eff;
		font-size: 15px;
		padding: 8px 16px;
	}
	.loading-ai-img {
		width: 24px;
		height: 24px;
		display: block;
	}
	.loading-ai-img {
		margin-right: 6px;
	}
	.ai-response-footer {
		display: flex;
		flex-direction: column;
		gap: 16px;

		// 文件标签区
		.file-tags {
			display: flex;
			flex-wrap: wrap;
			gap: 12px 8px;
			margin: 12px 0 0 0;
		}

		// 工具操作按钮区
		.tool-actions {
			display: flex;
			gap: 8px;
			margin: 12px 0 16px 0;

			.el-button {
				transition: all 0.2s ease;

				&:hover {
					transform: translateY(-1px);
					box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
				}

				&.is-loading {
					pointer-events: none;
				}
			}
		}

		// 推荐问题区
		.suggested-questions {
			display: flex;
			flex-direction: column;
			gap: 10px;
			margin-top: 10px;
		}
		.suggested-questions .question-item {
			background: #f7faff;
			border-radius: 6px;
			padding: 12px 16px;
			cursor: pointer;
			display: inline-flex;
			justify-content: space-between;
			align-items: center;
			font-size: 15px;
			color: #409eff;
			border: 1px solid #e6f7ff;
			transition: none;
			width: auto;
			max-width: 100%;
		}
		.suggested-questions .question-item:hover {
			background: #f7faff;
			color: #409eff;
			border: 1px solid #e6f7ff;
		}
	}
}
// 文件详情视图样式
.file-details-view {
	flex: 1; /* 占据剩余空间 */
	min-width: 0; /* 防止内容溢出 */
	display: flex;
	flex-direction: column;
	background-color: #f9f9f9;
	padding: 20px 40px;
	overflow-y: auto;

	.file-details-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
		padding-bottom: 20px;
		border-bottom: 1px solid #e0e0e0;
		flex-shrink: 0;

		.file-title-wrapper {
			display: flex;
			align-items: center;
			gap: 12px;
			font-size: 18px;
			font-weight: bold;
			color: #333;
			i {
				font-size: 20px;
				color: #409eff;
			}
		}
		.el-button {
			display: flex;
			align-items: center;
			gap: 4px;
		}
	}

	.file-details-body {
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 10px 0;

		// 加载状态
		.loading-container {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 20px;
			i {
				font-size: 24px;
				color: #409eff;
				margin-bottom: 8px;
			}
			span {
				font-size: 14px;
				color: #606266;
			}
		}

		// 空状态
		.empty-content {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 20px;
			i {
				font-size: 24px;
				color: #606266;
				margin-bottom: 8px;
			}
			span {
				font-size: 14px;
				color: #606266;
			}
		}

		.content-snippet-card {
			background: #fff;
			border: 1px solid #e0e0e0;
			border-radius: 8px;
			padding: 20px;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
			cursor: pointer;
			transition: box-shadow 0.2s, border-color 0.2s;
			&:hover {
				box-shadow: 0 4px 16px rgba(64, 158, 255, 0.08);
				border-color: #409eff;
			}

			.relevance-tag {
				width: 140px;
				text-align: center;
				background-color: #e6f7ff;
				color: #409eff;
				padding: 4px 10px;
				border-radius: 6px;
				display: inline-block;
				font-size: 12px;
				font-weight: bold;
				margin-bottom: 12px;
				cursor: pointer;
				display: flex;
				justify-content: space-between;
				align-items: center;
				transition: all 0.2s ease;

				&:hover {
					background-color: #409eff;
					color: #fff;
					transform: translateY(-1px);
					box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);

					i {
						color: #fff;
					}
				}

				i {
					font-size: 14px;
					color: #409eff;
					transition: color 0.2s ease;
				}
			}
			.snippet-text {
				font-size: 14px;
				line-height: 1.8;
				color: #606266;
			}
		}
	}
}
// 文件标签悬浮弹窗样式
// .file-details-popover {
//   min-width: 340px;
//   padding: 12px 16px;
//   border-radius: 8px;
//   box-shadow: 0 4px 16px rgba(0,0,0,0.08);
//   .popover-meta-table {
//     display: flex;
//     flex-direction: column;
//     gap: 6px;
//     .meta-row {
//       display: flex;
//       flex-wrap: wrap;
//       margin-bottom: 2px;
//       .meta-label {
//         color: #888;
//         min-width: 80px;
//         margin-right: 6px;
//         font-size: 14px;
//       }
//       .meta-value {
//         color: #222;
//         min-width: 90px;
//         margin-right: 24px;
//         font-size: 14px;
//       }
//     }
//   }
// }
// 下拉菜单样式
.el-dropdown-menu {
	.el-dropdown-menu__item {
		display: flex;
		align-items: center;
		gap: 8px;
		i {
			font-size: 14px;
		}
	}
}
// 文件详情页标题可点击样式
.link-to-detail {
	cursor: pointer;
	color: #4666a0;
	text-decoration: underline;
	transition: all 0.2s ease;

	&:hover {
		color: #66b1ff;
		text-decoration: none;
		transform: translateY(-1px);
	}
}
// 侧边栏折叠展开按钮样式
.sidebar-expand-btn {
	background: #fff;
	border-radius: 0 6px 6px 0;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	padding: 8px 10px;
	z-index: 1000;
	cursor: pointer;
	padding: 16px;
	text-align: center;
	.el-icon-s-unfold {
		font-size: 25px;
		display: block;
		padding: 20px 0;
		&:hover {
			color: #66b1ff;
		}
	}
}
// 优化 .quote-icon 和 .suggested-questions 样式
.quote-icon {
	font-size: 24px;
	color: #ff9900;
	font-weight: bold;
	margin-right: 8px;
	vertical-align: middle;
}
.suggested-questions {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 10px;
}
.suggested-questions .question-item {
	background: #f7faff;
	border-radius: 6px;
	padding: 12px 16px;
	cursor: pointer;
	display: inline-flex;
	justify-content: space-between;
	align-items: center;
	font-size: 15px;
	color: #409eff;
	border: 1px solid #e6f7ff;
	transition: none;
	width: auto;
	max-width: 100%;
}
.suggested-questions .question-item:hover {
	background: #f7faff;
	color: #409eff;
	border: 1px solid #e6f7ff;
}
.download-el-dialog {
	border-radius: 8px;
	min-width: 380px;
	min-height: 180px;
}
.tips-questions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	margin-bottom: 8px;
	.tips-title {
		color: #ff9900;
		font-weight: bold;
		margin-right: 8px;
	}
	.tips-item {
		background: #fffbe6;
		color: #d48806;
		border: 1px solid #ffe58f;
		margin-right: 8px;
		margin-bottom: 4px;
		transition: background 0.2s;
	}
	.tips-item:hover {
		background: #ffe58f;
		color: #ad6800;
	}
}
.padding16 {
	padding: 0px 16px;
}
.padding-bottom16 {
	padding-bottom: 16px;
}

// 自定义按钮
.custom-btn {
	padding: 6px !important;
	font-size: 18px !important;
	margin-left: 10px !important;
}
.select-tag {
	margin-bottom: 8px;
	font-size: 14px;
	color: #999;
	display: flex;
	align-items: center;
}
.guess-box {
	width: 90%;
	margin: 50px auto 20px;
	background-color: #fff;
	padding: 20px;
	border-radius: 20px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
	line-height: 36px;

	.color-primary {
		color: #409eff;
	}
}
</style>
